import { Add, Check, Checklist } from '@mui/icons-material'
import {
  Box,
  Button,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { ProjectDocumentRule, ProjectGridFieldRule } from '@/types/rules'
import useOverlay from '@/hooks/useOverlay'
import { useUpdateProjectDocumentRule } from '@/service-library/hooks/project-document-rules'
import { useUpdateProjectGridFieldRule } from '@/service-library/hooks/project-grid-field-rules'
import { isProjectGridFieldRule } from '@/components/rules/RuleSettings'

type RulesListProps<T extends ProjectGridFieldRule | ProjectDocumentRule> = {
  rules: T[]
  listQueryKey: QueryKey
  workflowStateId: string
  showManageButton?: boolean
  subheaderText?: string
}

export default function RulesList<
  T extends ProjectGridFieldRule | ProjectDocumentRule,
>({
  rules,
  listQueryKey,
  workflowStateId,
  showManageButton = false,
  subheaderText,
}: RulesListProps<T>) {
  const addRuleOverlay = useOverlay()

  const { updateProjectDocumentRule } = useUpdateProjectDocumentRule({
    listQueryKey,
  })
  const { updateProjectGridFieldRule } = useUpdateProjectGridFieldRule({
    listQueryKey,
  })

  const applied = rules.filter((rule) =>
    rule.workflow_states_ids.includes(workflowStateId),
  )

  const updateRuleInState = (rule: T) => {
    const updatedStateIds = rule.workflow_states_ids.includes(workflowStateId)
      ? rule.workflow_states_ids.filter((id) => id !== workflowStateId)
      : [...rule.workflow_states_ids, workflowStateId]

    const isFieldRule = isProjectGridFieldRule(rule)

    if (isFieldRule) {
      updateProjectGridFieldRule({
        ...rule,
        workflow_states_ids: updatedStateIds,
      })
    } else {
      updateProjectDocumentRule({
        ...rule,
        workflow_states_ids: updatedStateIds,
      })
    }
  }

  const disableActionMessage =
    rules.length === 0 ? 'No rules have been created for this field.' : ''

  return (
    <List dense disablePadding>
      {subheaderText && (
        <>
          <ListSubheader>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              {subheaderText}
              {!showManageButton && (
                <Tooltip
                  title={
                    disableActionMessage
                      ? disableActionMessage
                      : 'Manage Rules in State'
                  }
                  enterDelay={1000}
                >
                  <div>
                    <IconButton
                      disabled={!!disableActionMessage}
                      onClick={addRuleOverlay.open}
                      size="small"
                      sx={{ minHeight: 0, height: '100%' }}
                    >
                      <Checklist fontSize="inherit" />
                    </IconButton>
                  </div>
                </Tooltip>
              )}
            </Stack>
          </ListSubheader>
        </>
      )}

      {applied.map((appliedRule) => {
        const isFieldRule = isProjectGridFieldRule(appliedRule)
        const type = isFieldRule ? appliedRule.rule : appliedRule.type
        return (
          <ListItemButton
            key={appliedRule.id}
            LinkComponent={Link}
            href={`../${
              isFieldRule ? 'field-rules' : 'document-rules'
            }?rule_id=${appliedRule.id}`}
            target="_blank"
            rel="noreferrer"
            sx={{ pl: 4 }}
          >
            <ListItemText
              slotProps={{
                primary: {
                  component: Stack,
                  flexDirection: 'row',
                  alignItems: 'center',
                  columnGap: 1,
                  justifyContent: 'space-between',
                },
              }}
            >
              <Typography variant="inherit">
                {type.name}
                {appliedRule.params.function_name && (
                  <Typography variant="caption" color="secondary">
                    {' '}
                    ({appliedRule.params.function_name})
                  </Typography>
                )}
              </Typography>
              {!appliedRule.is_enabled && (
                <Typography variant="caption" color="text.secondary">
                  disabled
                </Typography>
              )}
            </ListItemText>
          </ListItemButton>
        )
      })}

      {showManageButton && (
        <Tooltip
          title={disableActionMessage}
          enterDelay={1000}
          placement="bottom-start"
        >
          <div>
            <Button
              disabled={!!disableActionMessage}
              variant="text"
              startIcon={<Add />}
              sx={{ m: 1 }}
              onClick={addRuleOverlay.open}
            >
              Manage Rules In State
            </Button>
          </div>
        </Tooltip>
      )}

      <Menu
        anchorEl={addRuleOverlay.anchorEl as Element}
        open={addRuleOverlay.isOpen}
        onClose={addRuleOverlay.close}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {rules.map((rule) => {
          const isFieldRule = isProjectGridFieldRule(rule)
          const type = isFieldRule ? rule.rule : rule.type
          return (
            <MenuItem
              dense
              key={rule.id}
              onClick={() => {
                updateRuleInState(rule)
              }}
            >
              <Stack direction="row" spacing={1}>
                {rule.workflow_states_ids?.includes(workflowStateId) ? (
                  <Check fontSize="small" />
                ) : applied.length > 0 ? (
                  <Box
                    sx={{
                      width: 20,
                    }}
                  />
                ) : null}
                <Typography variant="inherit">
                  {type.name}
                  {rule.params.function_name && (
                    <Typography variant="caption" color="secondary">
                      {' '}
                      ({rule.params.function_name})
                    </Typography>
                  )}
                  {!rule.is_enabled && (
                    <Typography variant="caption" color="text.secondary">
                      {' '}
                      (disabled)
                    </Typography>
                  )}
                </Typography>
              </Stack>
            </MenuItem>
          )
        })}
      </Menu>
    </List>
  )
}
